import React, { useState, useMemo, useCallback } from 'react';
import {
  Menu,
  Box,
  Badge,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Stack,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { axiosService } from 'shared/helpers/axios';

// Import your notification card components
import InvitationNotificationCard from 'components/comm/notifications/invitation/InvitationNotificationCard';
import SuggestionNotificationCard from 'components/comm/notifications/invitation/SuggestionNotificationCard';
import MeetingRequestNotificationCard from 'components/comm/notifications/meeting/MeetingRequestNotificationCard';
import CommentNotificationCard from 'components/comm/notifications/comment/CommentNotificationCard';
import LikeNotificationCard from 'components/comm/notifications/like/LikeNotificationCard';
import JoinRequestNotificationCard from 'components/comm/notifications/invitation/JoinRequestNotificationCard';
import FollowNotificationCard from 'components/comm/notifications/follow/FollowNotificationCard';
import VerificationBonusNotificationCard from 'components/comm/notifications/bonus/VerificationBonusNotificationCard';
import CallUpdateNotificationCard from 'components/comm/notifications/call/CallUpdateNotificationCard';
import ModerationWarningNotificationCard from 'components/comm/notifications/system/ModerationWarningNotificationCard';
import ModerationReportNotificationCard from 'components/comm/notifications/system/ModerationReportNotificationCard';
import ProposalReinviteNotificationCard from 'components/comm/notifications/proposal/ProposalReinviteNotificationCard';
import RoleAssignedNotificationCard from 'components/comm/notifications/proposal/RoleAssignedNotificationCard';
import ProposalChatNotificationCard from 'components/comm/notifications/proposal/ProposalChatNotificationCard';
import ManuscriptEditNotificationCard from 'components/comm/notifications/proposal/ManuscriptEditNotificationCard';
import LabNotificationCard from 'components/comm/notifications/lab/LabNotificationCard';
import EventNotificationCard from 'components/comm/notifications/event/EventNotificationCard';
import SecurityAlertCard from 'components/comm/notifications/security/SecurityAlertCard';
import PositionNotificationCard from 'components/comm/notifications/position/PositionNotificationCard';
import MentionNotificationCard from 'components/comm/notifications/mention/MentionNotificationCard';
import ConnectionNotificationCard from 'components/comm/notifications/connection/ConnectionNotificationCard';
import TaskAssignedNotificationCard from 'components/comm/notifications/proposal/TaskAssignedNotificationCard';
import TodoAssignedNotificationCard from 'components/comm/notifications/proposal/TodoAssignedNotificationCard';
import { useNotifications } from 'components/comm/notifications/contexts/NotificationContext';
import { formatDistanceToNow, parseISO, differenceInMinutes } from 'date-fns';



// Helper functions for notification categorization
const norm = (v) => (v || '').toString().toLowerCase();

const isCollaboration = (n) => {
  const t = norm(n.ntype);
  return t === 'consortium_invitation' || t === 'consortium_update' || t === 'invitation';
};

const isProposalReinvite = (n) => norm(n.ntype) === 'proposal_reinvite';

const isSuggestion = (n) => norm(n.ntype).includes('suggestion');
const isJoinRequest = (n) => norm(n.ntype).includes('consortium_join_request');

const isMeetingRequest = (n) => norm(n.ntype) === 'meeting_request';
const isMeetingPoll = (n) => norm(n.ntype) === 'meeting_poll';

const isComment = (n) => norm(n.ntype) === 'comment';
const isFollow = (n) => norm(n.ntype) === 'follow';


const isLike = (n) => {
  const t = norm(n.ntype);
  return t === 'like' || t === 'content_liked' || t.includes('like');
};

const isVerificationBonus = (n) => norm(n.ntype) === 'verification_bonus';
const isCallUpdate = (n) => norm(n.ntype) === 'call_update';
const isSystem = (n) => norm(n.ntype) === 'system';
const isModReport = (n) => norm(n.ntype) === 'moderation_report';
const isRoleAssigned = (n) => norm(n.ntype) === 'role_assigned';
const isTaskAssigned = (n) => norm(n.ntype) === 'task_assigned';
const isTodoAssigned = (n) => norm(n.ntype) === 'todo_assigned';
const isProposalChat = (n) => norm(n.ntype) === 'proposal_chat';
const isManuscriptEdit = (n) => norm(n.ntype) === 'proposal_update' && n.payload?.kind === 'manuscript_edit';

const NotificationMenu = ({ 
  notifications = [], 
  notificationAnchorEl, 
  isNotificationMenuOpen, 
  unreadCount = 0,
  onClose,
  onNotificationClick,
  onMarkAllAsRead,
  notificationMenuId = 'notification-menu'
}) => {
  const navigate = useNavigate();
    const { handleNotificationAction } = useNotifications();
  // UI state
  const [searchQuery, setSearchQuery] = useState('');

  // Get notifications list
  const getNotificationsList = useCallback(() => {
    if (!notifications) return [];
    
    if (Array.isArray(notifications)) {
      return notifications;
    }
    
    if (notifications.results && Array.isArray(notifications.results)) {
      return notifications.results;
    }
    
    return [];
  }, [notifications]);

  const notificationsList = getNotificationsList();

  // Sort notifications by date (newest first)
  const sortedNotifications = useMemo(
    () => [...notificationsList].sort((a, b) => 
      new Date(b.created || b.timestamp) - new Date(a.created || a.timestamp)
    ),
    [notificationsList]
  );

  // Search filter
  const matchesSearch = useCallback((notification) => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const payload = notification.payload || {};
    
    const searchableText = [
      notification.title,
      notification.message,
      notification.ntype,
      payload.consortium_name,
      payload.from_user_name,
      payload.body_preview,
      payload.title,
      payload.message
    ].filter(Boolean).join(' ').toLowerCase();
    
    return searchableText.includes(query);
  }, [searchQuery]);

  // Filtered notifications (search only, no tabs)
  const filteredNotifications = useMemo(() => 
    sortedNotifications.filter(matchesSearch),
    [sortedNotifications, matchesSearch]
  );

  // Unread count in current view
  const unreadInView = useMemo(() => 
    filteredNotifications.filter(n => !n.is_read).length,
    [filteredNotifications]
  );

  // Mark all visible as read
  const markAllVisibleAsRead = useCallback(async () => {
    const unreadInView = filteredNotifications.filter(n => !n.is_read);
    
    for (const notification of unreadInView) {
      try {
        await axiosService.patch(`/notifications/${notification.id}/`, {
          is_read: true,
        });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    
    // Call parent's mark all as read if provided
    if (onMarkAllAsRead && unreadInView.length > 0) {
      onMarkAllAsRead();
    }
  }, [filteredNotifications, onMarkAllAsRead]);

  // Render each notification using the appropriate card component
  const renderNotification = (notification) => {
    // Define markAsRead function for the card components

  //   if (minutesAgo < 10) {

  //     console.log('### Notification created less than an hour ago');
  //     console.log('### Formatted time:', timeAgo);
  //     console.log('### Created at:', notification.created, 'Parsed:', createdAt);
  //     console.log('### Current time:', now);
  //     console.log('### Minutes ago:', minutesAgo);
  // }
// console.log('##### ntype', notification.ntype, 'timeAgo:', timeAgo, 'created:', notification.created);

    const markAsRead = () => {
      axiosService.patch(`/notifications/${notification.id}/`, {
        is_read: true,
      }).catch(error => {
        console.error('Error marking notification as read:', error);
      });
    };

    // Add handleNotificationAction for compatibility
    // const handleNotificationAction = (e) => {
    //   // This can be customized based on your needs
    //   e.stopPropagation();
    //   console.log('Notification action triggered');
    // };





    // Default: Collaboration/Invitation card
 
  

    
    if (isLike(notification)) {
      return (
        <LikeNotificationCard
          key={notification.id}
          notification={notification}
          onMarkRead={markAsRead}
          sx={{ 
            mb: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        />
      );
    }
    
    if (isComment(notification)) {
      return (
        <CommentNotificationCard
          key={notification.id}
          notification={notification}
          onMarkRead={markAsRead}
          sx={{ 
            mb: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        />
      );
    }

    if (isMeetingRequest(notification) || isMeetingPoll(notification)) {
      return (
        <MeetingRequestNotificationCard
          key={notification.id}
          notification={notification}
          onMarkAsRead={markAsRead}
          handleNotificationAction={handleNotificationAction}
          sx={{ 
            mb: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        />
      );
    }
    
    if (isSuggestion(notification)) {
      return (
        <SuggestionNotificationCard
          key={notification.id}
          notification={notification}
          onMarkRead={markAsRead}
          handleNotificationAction={handleNotificationAction}
          sx={{ 
            mb: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        />
      );
    }
    

    // isJoinRequest(notification) && console.log('### Rendering JoinRequestNotificationCard for notification:', notification);

     if (isJoinRequest(notification)) {
      return (
        <JoinRequestNotificationCard
          key={notification.id}
          notification={notification}
          onMarkRead={markAsRead}
          handleNotificationAction={handleNotificationAction}
          sx={{ 
            mb: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        />
      );
    } 




  if (isCallUpdate(notification)) {
    return (
      <CallUpdateNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
      />
    );
  }

  if (isVerificationBonus(notification)) {
    return (
      <VerificationBonusNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
      />
    );
  }

  if (isFollow(notification)) {
    return (
      <FollowNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
        sx={{
          mb: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' }
        }}
        />
      );
    }
  

  if (isModReport(notification)) {
    return (
      <ModerationReportNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
      />
    );
  }

  if (isSystem(notification)) {
    return (
      <ModerationWarningNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
      />
    );
  }

  if (isProposalReinvite(notification)) {
    return (
      <ProposalReinviteNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={() => markAsRead(notification.id)}
        handleNotificationAction={handleNotificationAction}
      />
    );
  }

  if (isRoleAssigned(notification)) {
    return (
      <RoleAssignedNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
      />
    );
  }

  if (isTaskAssigned(notification)) {
    return (
      <TaskAssignedNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
      />
    );
  }

  if (isTodoAssigned(notification)) {
    return (
      <TodoAssignedNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
      />
    );
  }

  if (isProposalChat(notification)) {
    return (
      <ProposalChatNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
      />
    );
  }

  // someone asked to connect with you (Accept / Decline right here), or accepted your request
  if (['follow_request', 'follow_accepted'].includes(norm(notification.ntype))) {
    return <ConnectionNotificationCard key={notification.id} notification={notification} onMarkRead={markAsRead} />;
  }

  if (norm(notification.ntype) === 'mention') {
    return <MentionNotificationCard key={notification.id} notification={notification} onMarkRead={markAsRead} />;
  }

  if (norm(notification.ntype) === 'position_update') {
    return <PositionNotificationCard key={notification.id} notification={notification} onMarkRead={markAsRead} />;
  }

  if (norm(notification.ntype) === 'security_alert') {
    return <SecurityAlertCard key={notification.id} notification={notification} onMarkRead={markAsRead} />;
  }

  if (norm(notification.ntype) === 'lab_update') {
    return <LabNotificationCard key={notification.id} notification={notification} onMarkRead={markAsRead} />;
  }

  if (norm(notification.ntype) === 'event_update') {
    return <EventNotificationCard key={notification.id} notification={notification} onMarkRead={markAsRead} />;
  }

  if (isManuscriptEdit(notification)) {
    return (
      <ManuscriptEditNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={markAsRead}
      />
    );
  }

  if (isCollaboration(notification)) {
    return (
      <InvitationNotificationCard
        key={notification.id}
        notification={notification}
        onMarkRead={() => markAsRead(notification.id)}
        handleNotificationAction={handleNotificationAction}
      />
    );
  }

  return null;
}

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleViewAll = () => {
    navigate('/notifications/');
    handleClose();
  };

  return (
    <Menu
      anchorEl={notificationAnchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={notificationMenuId}
      keepMounted
      open={isNotificationMenuOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          mt: 1,
          minWidth: 480,
          maxWidth: 520,
          maxHeight: 600,
          overflow: 'hidden',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.16)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight={600}>
            Notifications
            {unreadCount > 0 && (
              <Badge 
                badgeContent={unreadCount} 
                color="error" 
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              size="small" 
              onClick={handleClose}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
        </Stack>

        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search notifications…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mt: 2 }}
        />
      </Box>

      {/* Mark All Read Button */}
      {unreadInView > 0 && (
        <Box sx={{ 
          p: 1, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex', 
          justifyContent: 'flex-end'
        }}>
          <Button
            size="small"
            variant="text"
            startIcon={<MarkEmailReadIcon />}
            onClick={markAllVisibleAsRead}
            sx={{ fontSize: '0.75rem' }}
          >
            Mark all as read ({unreadInView})
          </Button>
        </Box>
      )}

      {/* Notification List - Using specific card components */}
      <Box sx={{ 
        maxHeight: 350, 
        overflow: 'auto',
        p: 1
      }}>
        {filteredNotifications.length === 0 ? (
          <Box sx={{ 
            py: 6, 
            textAlign: 'center', 
            color: 'text.secondary' 
          }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {searchQuery ? 'No matching notifications' : 'No notifications'}
            </Typography>
            <Typography variant="body2">
              {searchQuery 
                ? 'Try a different search term' 
                : 'You\'re all caught up!'}
            </Typography>
          </Box>
        ) : (
          filteredNotifications.map(renderNotification)
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ 
        borderTop: 1, 
        borderColor: 'divider',
        p: 1
      }}>
        <Button
          fullWidth
          variant="text"
          size="small"
          onClick={handleViewAll}
          sx={{ 
            textAlign: 'center',
            fontSize: '0.875rem',
            fontWeight: 600
          }}
        >
          View all notifications
        </Button>
      </Box>
    </Menu>
  );
};

export default NotificationMenu;